import { useState } from 'react'
import {
  Eye,
  ShieldCheck,
  Workflow,
  Radio,
  Sparkles,
  ChevronRight,
} from 'lucide-react'
import virtualOfficeIsometric from '../../images/virtual_office_isometric_1788960173551.jpg'
const VirtualOfficeSection = () => {
  const [activeZone, setActiveZone] = useState(0)
  const [viewMode, setViewMode] = useState('isometric')
  const zones = [
    {
      title: 'Despacho de Direcci\xF3n & Orquestaci\xF3n',
      agent: 'Director de Marketing IA',
      status: 'Activo \u2022 Validando KPIs de 10 cuentas',
      desc: 'El cerebro estrat\xE9gico de la oficina. Prioriza sprints, distribuye briefings de clientes y aprueba entregables antes de su publicaci\xF3n.',
      coords: 'Zona Superior / Sala Ejecutiva',
      tag: 'Gobernanza',
    },
    {
      title: 'Laboratorio de Creatividad & Copy',
      agent: 'Valentina (Copy) + Isaura (Dise\xF1o)',
      status: 'Produciendo 14 creatividades simult\xE1neas',
      desc: 'Mesa colaborativa continua. Valentina env\xEDa variantes de copy a Isaura, quien compone carruseles y banners respetando las gu\xEDas de cada marca.',
      coords: 'Ala Este / Mesa Creativa',
      tag: 'Producci\xF3n',
    },
    {
      title: 'Trading Floor de Performance & Ads',
      agent: 'Alexa (Paid Media) + Geralis (Data)',
      status: 'Monitorizando Meta, Google y TikTok Ads en vivo',
      desc: 'Panel de control con conexi\xF3n API directa. Supervisa subastas en milisegundos, apaga creatividades fatigadas e inyecta presupuesto al ganador.',
      coords: '\xC1rea Central / Estaciones de Trabajo',
      tag: 'ROAS M\xE1ximo',
    },
    {
      title: 'B\xF3veda de Datos & Memoria Multi-Cliente',
      agent: 'Sistema de Aislamiento Seguro Hutrit',
      status: '10 entornos sellados y encriptados',
      desc: 'Separaci\xF3n estricta de bases de datos. Los datos confidenciales, historiales y gu\xEDas del Cliente A jam\xE1s se mezclan con los del Cliente B.',
      coords: 'Rack de Servidores / Seguridad',
      tag: 'Aislamiento 100%',
    },
  ]
  const clientStreams = [
    {
      client: 'Cliente #01 (Cl\xEDnica Dental Premium)',
      task: 'Alexa reoptimiz\xF3 campa\xF1a local de Google Ads',
      time: 'hace 2 min',
      status: 'Completado',
    },
    {
      client: 'Cliente #02 (SaaS B2B Legaltech)',
      task: 'Valentina gener\xF3 4 secuencias de Cold Email',
      time: 'hace 5 min',
      status: 'En validaci\xF3n',
    },
    {
      client: 'Cliente #03 (E-commerce Cosm\xE9tica)',
      task: 'Isaura renderiz\xF3 6 creatividades para Reels',
      time: 'hace 8 min',
      status: 'Listo',
    },
    {
      client: 'Cliente #04 (Franquicia Fitness)',
      task: 'Auditor\xEDa mensual de gasto compilada por Geralis',
      time: 'hace 11 min',
      status: 'Completado',
    },
    {
      client: 'Cliente #05 (Inmobiliaria de Lujo)',
      task: 'Director IA ajust\xF3 calendario de contenidos SEO',
      time: 'hace 15 min',
      status: 'Activo',
    },
  ]
  return (
    <section
      id="oficina-virtual"
      className="py-24 relative bg-[#011e1b] border-t border-[#00FF9D]/15 overflow-hidden"
    >
      {/* Background ambient lighting */}
      <div
        className="absolute top-1/2 left-1/3 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[140px] pointer-events-none opacity-20"
        style={{
          background: 'radial-gradient(circle, #00FF9D 0%, transparent 70%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#05332E] border border-[#00FF9D]/30 text-xs font-mono text-[#00FF9D] uppercase tracking-wider mb-4">
            <Radio className="w-3.5 h-3.5 animate-pulse text-[#00FF9D]" />
            Central de Mando Isométrica
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-5">
            Visualiza, asigna y supervisa a tu equipo de IA en{' '}
            <span className="text-[#00FF9D]">tiempo real</span>
          </h2>
          <p className="text-base sm:text-lg text-[#CBD5E1] leading-relaxed">
            Tu agencia cuenta con una Oficina Virtual propia, una central de
            mando en vista isométrica/dashboard donde cada agente trabaja de
            forma coordinada las 24 horas del día.
          </p>

          {/* 3 Core Points from requirements */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10 text-left">
            <div className="p-4 rounded-xl bg-[#05332E]/80 border border-[#00FF9D]/20 shadow-md">
              <div className="w-8 h-8 rounded-lg bg-[#00FF9D]/15 text-[#00FF9D] flex items-center justify-center mb-3">
                <Workflow className="w-4 h-4" />
              </div>
              <h4 className="text-sm font-bold text-white mb-1">
                Flujos Autónomos
              </h4>
              <p className="text-xs text-[#CBD5E1] leading-relaxed">
                Los agentes se comunican entre sí en segundo plano sin requerir
                micro-gestión manual.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-[#05332E]/80 border border-[#00FF9D]/20 shadow-md">
              <div className="w-8 h-8 rounded-lg bg-[#00FF9D]/15 text-[#00FF9D] flex items-center justify-center mb-3">
                <Eye className="w-4 h-4" />
              </div>
              <h4 className="text-sm font-bold text-white mb-1">
                Visibilidad Total
              </h4>
              <p className="text-xs text-[#CBD5E1] leading-relaxed">
                Controla el estado de los proyectos, tareas y entregas en vivo
                con telemetría de segundos.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-[#05332E]/80 border border-[#00FF9D]/20 shadow-md">
              <div className="w-8 h-8 rounded-lg bg-[#00FF9D]/15 text-[#00FF9D] flex items-center justify-center mb-3">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <h4 className="text-sm font-bold text-white mb-1">
                Multi-cliente e Aislado
              </h4>
              <p className="text-xs text-[#CBD5E1] leading-relaxed">
                Segmentación absoluta y hermética de la información de cada uno
                de tus 10 clientes.
              </p>
            </div>
          </div>
        </div>

        {/* Interactive Virtual Office Showcase Window */}
        <div className="rounded-2xl border-2 border-[#00FF9D]/30 bg-[#021A17] shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden">
          {/* Top Office Nav Bar */}
          <div className="px-5 py-3.5 bg-[#05332E] border-b border-[#00FF9D]/20 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500/80" />
                <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <span className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <span className="text-xs font-mono text-white font-semibold flex items-center gap-2">
                <span>Hutrit Virtual Office v2.4</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#00FF9D]/20 text-[#00FF9D] border border-[#00FF9D]/30">
                  9 Agentes Operando 24/7
                </span>
              </span>
            </div>

            {/* View Mode Switcher */}
            <div className="flex items-center bg-[#012622] rounded-lg p-1 border border-[#00FF9D]/20 text-xs font-mono">
              <button
                onClick={() => setViewMode('isometric')}
                className={`px-3 py-1 rounded-md transition-all ${
                  viewMode === 'isometric'
                    ? 'bg-[#00FF9D] text-[#012622] font-bold shadow-sm'
                    : 'text-[#CBD5E1] hover:text-white'
                }`}
              >
                Vista Isométrica 24/7
              </button>
              <button
                onClick={() => setViewMode('workstreams')}
                className={`px-3 py-1 rounded-md transition-all ${
                  viewMode === 'workstreams'
                    ? 'bg-[#00FF9D] text-[#012622] font-bold shadow-sm'
                    : 'text-[#CBD5E1] hover:text-white'
                }`}
              >
                Matriz Multi-Cliente (10)
              </button>
            </div>
          </div>

          {/* Main Visual Display */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Left/Center Visual Canvas */}
            <div className="lg:col-span-8 relative bg-[#011412] p-4 sm:p-6 flex items-center justify-center min-h-[420px] overflow-hidden">
              {viewMode === 'isometric' ? (
                <div className="relative w-full h-full flex flex-col items-center justify-center">
                  {/* Generated Isometric Pixel Art Virtual Office Image */}
                  <div className="relative rounded-xl overflow-hidden border border-[#00FF9D]/30 shadow-2xl w-full group">
                    <img
                      src={virtualOfficeIsometric}
                      alt="Oficina Virtual Isométrica 24/7 de Hutrit"
                      className="w-full h-auto object-cover max-h-[460px] filter saturate-[1.1] transition-transform duration-500 group-hover:scale-[1.02]"
                      referrerPolicy="no-referrer"
                    />

                    {/* Interactive Hotspot Pills positioned over office zones */}
                    <div
                      onClick={() => setActiveZone(0)}
                      className="absolute top-10 left-12 sm:left-24 bg-[#012622]/90 hover:bg-[#00FF9D] hover:text-[#012622] text-[#00FF9D] border border-[#00FF9D] px-2.5 py-1 rounded-full text-[11px] font-mono font-bold shadow-lg flex items-center gap-1.5 cursor-pointer transition-all transform hover:scale-105"
                    >
                      <span className="w-2 h-2 rounded-full bg-[#00FF9D] animate-ping" />
                      Dir. Estratégica
                    </div>

                    <div
                      onClick={() => setActiveZone(1)}
                      className="absolute top-1/2 left-8 sm:left-16 bg-[#012622]/90 hover:bg-[#00FF9D] hover:text-[#012622] text-[#00FF9D] border border-[#00FF9D] px-2.5 py-1 rounded-full text-[11px] font-mono font-bold shadow-lg flex items-center gap-1.5 cursor-pointer transition-all transform hover:scale-105"
                    >
                      <span className="w-2 h-2 rounded-full bg-[#00FF9D]" />
                      Copy & Diseño
                    </div>

                    <div
                      onClick={() => setActiveZone(2)}
                      className="absolute bottom-16 right-12 sm:right-24 bg-[#012622]/90 hover:bg-[#00FF9D] hover:text-[#012622] text-[#00FF9D] border border-[#00FF9D] px-2.5 py-1 rounded-full text-[11px] font-mono font-bold shadow-lg flex items-center gap-1.5 cursor-pointer transition-all transform hover:scale-105"
                    >
                      <span className="w-2 h-2 rounded-full bg-[#00FF9D] animate-pulse" />
                      Trading & Ads
                    </div>

                    <div
                      onClick={() => setActiveZone(3)}
                      className="absolute top-12 right-8 sm:right-20 bg-[#012622]/90 hover:bg-[#00FF9D] hover:text-[#012622] text-[#00FF9D] border border-[#00FF9D] px-2.5 py-1 rounded-full text-[11px] font-mono font-bold shadow-lg flex items-center gap-1.5 cursor-pointer transition-all transform hover:scale-105"
                    >
                      <span className="w-2 h-2 rounded-full bg-[#00FF9D]" />
                      Bóveda 10 Clientes
                    </div>
                  </div>

                  <div className="mt-3 text-[11px] font-mono text-[#CBD5E1]/60 flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-[#00FF9D]" />
                    <span>
                      Haz clic en cualquiera de las zonas del mapa para
                      inspeccionar a los agentes activos
                    </span>
                  </div>
                </div>
              ) : (
                /* Workstreams Tab View */
                <div className="w-full space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-[#0A3F39]">
                    <span className="text-xs font-mono font-bold text-[#00FF9D] uppercase">
                      Segmentación Multi-Cliente (Canales Aislados)
                    </span>
                    <span className="text-[11px] text-[#CBD5E1]/70 font-mono">
                      10 / 10 Clientes Operativos
                    </span>
                  </div>

                  {clientStreams.map((stream, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-xl bg-[#05332E]/70 border border-[#00FF9D]/20 flex items-center justify-between hover:bg-[#0A3F39]/60 transition-colors"
                    >
                      <div>
                        <div className="text-xs font-bold text-white flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00FF9D]" />
                          {stream.client}
                        </div>
                        <div className="text-[11px] text-[#CBD5E1] mt-0.5">
                          {stream.task}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] px-2 py-0.5 rounded bg-[#012622] text-[#00FF9D] font-mono border border-[#00FF9D]/30 block mb-0.5">
                          {stream.status}
                        </span>
                        <span className="text-[10px] text-[#CBD5E1]/50 font-mono">
                          {stream.time}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Sidebar: Active Zone Controller */}
            <div className="lg:col-span-4 bg-[#031E1B] p-5 sm:p-6 border-t lg:border-t-0 lg:border-l border-[#00FF9D]/20 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono text-[#00FF9D] font-bold uppercase tracking-wider">
                    Panel de Telemetría
                  </span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#05332E] text-white">
                    {zones[activeZone].coords}
                  </span>
                </div>

                <div className="p-4 rounded-xl bg-[#05332E] border border-[#00FF9D]/30 mb-5">
                  <div className="text-[11px] font-mono text-[#00FF9D] mb-1">
                    {zones[activeZone].tag}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {zones[activeZone].title}
                  </h3>
                  <div className="text-xs font-mono text-white/90 mb-2 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#00FF9D] animate-ping" />
                    <span>{zones[activeZone].agent}</span>
                  </div>
                  <p className="text-xs text-[#CBD5E1] leading-relaxed">
                    {zones[activeZone].desc}
                  </p>
                </div>

                {/* Quick Zone Selector Buttons */}
                <div className="space-y-2">
                  <div className="text-xs font-mono text-[#CBD5E1]/70 mb-1">
                    Zonas de la Oficina:
                  </div>
                  {zones.map((z, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveZone(idx)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-between ${
                        activeZone === idx
                          ? 'bg-[#00FF9D] text-[#012622] font-bold'
                          : 'bg-[#05332E]/40 text-[#CBD5E1] hover:bg-[#05332E] hover:text-white border border-white/5'
                      }`}
                    >
                      <span className="truncate">{z.title}</span>
                      <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Bottom security guarantee */}
              <div className="mt-6 pt-4 border-t border-[#0A3F39] text-xs font-mono text-[#CBD5E1]/80 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#00FF9D] flex-shrink-0" />
                <span>
                  Privacidad estricta: Los modelos no se entrenan con tus datos
                  de cliente.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
export { VirtualOfficeSection }
